// import ZoomToolBar from "@/components/zoomSlider.vue";
import paper from "paper";

import Registry from "../core/registry";
import * as Colors from "./colors";
import { saveAs } from "file-saver";

import Device from "../core/device";
import SelectTool from "./tools/selectTool";
import InsertTextTool from "./tools/insertTextTool";
import SimpleQueue from "../utils/simpleQueue";
import MouseSelectTool from "./tools/mouseSelectTool";
// import RenderMouseTool from "./tools/renderMouseTool";

import DXFObject from "../core/dxfObject";
import EdgeFeature from "../core/edgeFeature";
import * as HTMLUtils from "../utils/htmlUtils";
import MouseAndKeyboardHandler from "./mouseAndKeyboardHandler";
import DesignHistory from "./designHistory";
import MoveTool from "./tools/moveTool";
import ComponentPositionTool from "./tools/componentPositionTool";
import MultilayerPositionTool from "./tools/multilayerPositionTool";
import MultilevelPositionTool from "./tools/multilevelPositionTool";
import CellPositionTool from "./tools/cellPositionTool";
import ValveInsertionTool from "./tools/valveInsertionTool";
import PositionTool from "./tools/positionTool";
import ConnectionTool from "./tools/connectionTool";
import GenerateArrayTool from "./tools/generateArrayTool";
import CustomComponentManager from "./customComponentManager";
import CustomComponentPositionTool from "./tools/customComponentPositionTool";
import { setButtonColor } from "../utils/htmlUtils";
import PaperView from "./paperView";
import AdaptiveGrid from "./grid/adaptiveGrid";
import DAFDPlugin from "../plugin/dafdPlugin";
import { Examples } from "../index";
import Feature from "../core/feature";
import Layer from "../core/layer";
import Component from "../core/component";
import EventBus from "@/events/events";
import { ComponentAPI } from "@/componentAPI";
import { exitCanvasSettingsLikeEscape } from "@/utils/exitCanvasUi";
import RenderLayer from "@/app/view/renderLayer";

import LoadUtils from "@/app/utils/loadUtils";
import ExportUtils, { SerializationError } from "@/app/utils/exportUtils";
import {
    cloneImportedDeviceJson,
    findImportedComponentById as lookupImportedComponent,
    findImportedConnectionById as lookupImportedConnection,
    resolveResetParams
} from "@/app/utils/importedReset";
import { LogicalLayerType, InterchangeV1_2, ValveType } from "@/app/core/init";
import { stripToFlowControlPorts } from "@/app/utils/portsOnlyUtils";
import { ValveGapRect, valveGapRectFromValues } from "@/app/utils/valveChannelClip";
import JSZip from "jszip";

import { Point } from "@/app/core/init";

/**
 * View manager class
 */
import { MultiplyOperation } from "three";
import UIElement from "./uiElement";
import Connection from "../core/connection";
import Params from "../core/params";
import MouseTool from "./tools/mouseTool";
import { generateRenderLayers } from "../utils/renderUtils";
import MapUtils from "../utils/mapUtils";

const VALVE_RENDER_TYPES = new Set(["Valve", "Valve3D", "Valve3D_control"]);
const VALVE_BREAK_RELEVANT_PARAMS = new Set(["position", "rotation", "gap", "valveRadius", "width", "length"]);

export default class ViewManager {
    view: PaperView;
    renderLayers: RenderLayer[];
    activeRenderLayerIndex: number;
    nonphysElements: UIElement[];
    tools: { [k: string]: any };
    rightMouseTool: MouseTool;
    updateQueue: any;
    saveQueue: any;
    undoStack: any;
    pasteboard: any[];
    mouseAndKeyboardHandler: MouseAndKeyboardHandler;
    minZoom: number;
    maxZoom: number;
    threeD: boolean;
    renderer: any;

    messageBox?: any;
    customComponentManager?: CustomComponentManager;
    currentSelection?: any;

    private __grid: AdaptiveGrid;
    private __currentDevice: Device | null;
    private __button2D?: HTMLButtonElement;
    private __button3D?: HTMLButtonElement;
    private __canvasBlock?: HTMLCanvasElement;
    private __renderBlock?: HTMLCanvasElement;

    /** Cover-layer preview: canvas shows only FLOW/CONTROL port circles. */
    portsOnlyView = false;

    /** Deep clone of the last loaded device JSON, used to Reset placed objects to upload values. */
    importedSourceJson: InterchangeV1_2 | null = null;

    // TODO : Check if we can remove this tracking
    currentLayer: RenderLayer;

    /**
     * Default ViewManger Constructor
     */
    constructor() {
        this.view = new PaperView("c", this);
        this.__grid = new AdaptiveGrid(this);
        Registry.currentGrid = this.__grid;
        this.renderLayers = [];
        this.nonphysElements = []; // TODO - Keep track of what types of objects fall here UIElements
        this.tools = {};
        this.rightMouseTool = new SelectTool(this);
        this.__currentDevice = null;
        const reference = this;
        this.updateQueue = new SimpleQueue(function() {
            reference.view.refresh();
        }, 20);

        this.saveQueue = new SimpleQueue(function() {
            reference.saveToStorage();
        });

        this.undoStack = new DesignHistory();
        this.pasteboard = [];

        this.mouseAndKeyboardHandler = new MouseAndKeyboardHandler(this);

        this.view.setResizeFunction(function() {
            reference.updateGrid();
            reference.updateAlignmentMarks();

            reference.view.updateRatsNest();
            reference.view.updateComponentPortsRender();
            reference.updateDevice(Registry.currentDevice!);
        });

        const func = function(event: any) {
            reference.adjustZoom(event.deltaY, (reference.getEventPosition(event) as unknown) as number[]);
        };

        // this.manufacturingPanel = new ManufacturingPanel(this);

        // this.exportPanel = new ExportPanel(this);

        this.view.setMouseWheelFunction(func);
        this.minZoom = 0.0001;
        this.maxZoom = 5;
        this.setupTools();
        const ref = this;
        EventBus.get().on(EventBus.UPDATE_RENDERS, function(feature, refresh = true, updatedKey: string | null = null) {
            if (ref.ensureFeatureExists(feature)) {
                ref.view.updateFeature(feature);
                ref.syncConnectionBreaksForFeatureUpdate(feature, updatedKey);
                ref.refresh(refresh);
            }
        });

        // TODO: Figure out how remove UpdateQueue as dependency mechanism
        this.__grid.setColor(Colors.BLUE_500);

        // Removed from Page Setup
        this.threeD = false;
        this.renderer = Registry.threeRenderer;
        // this.__button2D = document.getElementById("button_2D");
        // this.__canvasBlock = document.getElementById("canvas_block");
        // this.__renderBlock = document.getElementById("renderContainer");
        // this.setupDragAndDropLoad("#c");
        // this.setupDragAndDropLoad("#renderContainer");
        this.currentLayer = this.renderLayers[0];
        this.activeRenderLayerIndex = 0;

    }


    /**
     * Returns the current device the ViewManager is displaying. Right now I'm using this to replace the
     * Registry.currentDevice dependency, however this might change as the modularity requirements change.
     *
     * @return {Device}
     * @memberof ViewManager
     */
    get currentDevice(): Device | null {
        return this.__currentDevice;
    }

    /**
     * Initiates the copy operation on the selected feature
     * @returns {void}
     * @memberof ViewManager
     */
    initiateCopy(): void  {
        const selectedFeatures = this.view.getSelectedFeatures();
        if (selectedFeatures.length > 0) {
            this.pasteboard[0] = selectedFeatures[0];
        }
    }

    /**
     * Initiating the zoom toolbar
     * @memberof ViewManager
     * @returns {void}
     */
    setupToolBars(): void  {
        // Initiating the zoom toolbar
        // this.zoomToolBar = new ZoomToolBar(0.0001, 5);
        // this.componentToolBar = new ComponentToolBar(this);
        this.resetToDefaultTool();
    }

    /**
     * Sets the initial state of the name map
     * @memberof ViewManager
     * @returns {void}
     */
    setNameMap(): void  {
        const newMap = new Map();
        for (let i = 0; i < this.currentDevice!.layers.length; i++) {
            const [nameStr, nameNum] = this.currentDevice!.layers[i].name.split("_");
            if (newMap.has(nameStr)) {
                if (newMap.get(nameStr) < nameNum) newMap.set(nameStr, parseInt(nameNum));
            } else {
                newMap.set(nameStr, parseInt(nameNum));
            }
        }
        for (let i = 0; i < this.currentDevice!.connections.length; i++) {
            const [nameStr, nameNum] = this.currentDevice!.connections[i].name.split("_");
            if (newMap.has(nameStr)) {
                if (newMap.get(nameStr) < nameNum) newMap.set(nameStr, parseInt(nameNum));
            } else {
                newMap.set(nameStr, parseInt(nameNum));
            }
        }
        for (let i = 0; i < this.currentDevice!.components.length; i++) {
            const [nameStr, nameNum] = this.currentDevice!.components[i].name.split("_");
            if (newMap.has(nameStr)) {
                if (newMap.get(nameStr) < nameNum) newMap.set(nameStr, parseInt(nameNum));
            } else {
                newMap.set(nameStr, parseInt(nameNum));
            }
        }
        for (let i = 0; i < this.renderLayers.length; i++) {
            const [nameStr, nameNum] = this.renderLayers[i].name.split("_");
            if (newMap.has(nameStr)) {
                if (newMap.get(nameStr) < nameNum) newMap.set(nameStr, parseInt(nameNum));
            } else {
                newMap.set(nameStr, parseInt(nameNum));
            }
        }

        this.currentDevice!.nameMap = newMap;
    }

    /**
     * Adds a device to the view manager
     * @param {Device} device Device to be added
     * @param {Boolean} refresh Default true
     * @memberof ViewManager
     * @returns {void}
     */
    addDevice(device: Device, refresh = true): void  {
        this.view.addDevice(device);
        this.__addAllDeviceLayers(device, false);
        this.refresh(refresh);
    }

    /**
     * Adds all the layers in the device
     * @param {Device} device Selected device
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @memberof ViewManager
     * @returns {void}
     * @private
     */
    __addAllDeviceLayers(device: Device, refresh = true): void  {
        for (let i = 0; i < device.layers.length; i++) {
            const layer = device.layers[i];
            this.addLayer(layer, i, false);
        }
    }

    /**
     * Removes all layers in the device
     * @param {Device} device Selected device
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @memberof ViewManager
     * @returns {void}
     */
    __removeAllDeviceLayers(device: Device, refresh = true): void  {
        for (let i = 0; i < device.layers.length; i++) {
            const layer = device.layers[i];
            this.removeLayer(layer, i, false);
        }
    }

    /**
     * Removes the device from the view
     * @param {Device} device Selected device to remove
     * @param {Boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    removeDevice(device: Device, refresh = true): void  {
        this.view.removeDevice();
        this.__removeAllDeviceLayers(device, false);
        this.refresh(refresh);
    }

    /**
     * Updates the device in the view
     * @param {Device} device Selected device to update
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    updateDevice(device: Device, refresh = true): void  {
        this.view.updateDevice(device);
        this.refresh(refresh);
    }

    /**
     * Adds a feature to the view
     * @param {Feature} feature Feature to add
     * @param {Boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    addFeature(feature: Feature, index = this.activeRenderLayerIndex, isPhysicalFlag = true, refresh = true): void  {
        // let isPhysicalFlag = true;
        this.renderLayers[index as number].addFeature(feature, isPhysicalFlag);
        if (this.ensureFeatureExists(feature)) {
            this.view.addFeature(feature);
            this.refresh(refresh);
        }
    }

    /**
     * Returns the component identified by the id
     * @param {string} id ID of the feature to get the component
     * @return {UIElement|null}
     * @memberof ViewManager
     */
    getNonphysElementFromFeatureID(id: string): UIElement | null {
        for (const i in this.nonphysElements) {
            const element = this.nonphysElements[i];
            // go through each component's features
            for (const j in element.featureIDs) {
                if (element.featureIDs[j] === id) {
                    return element;
                }
            }
        }

        return null;
    }

    /**
     * Updates a feature from the view
     * @param {Feature} feature Feature to update
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    updateFeature(feature: Feature, refresh = true): void  {
        if (this.ensureFeatureExists(feature)) {
            this.view.updateFeature(feature);
            this.refresh(refresh);
        }
    }

    /**
     * Removes feature from the view
     * @param {Feature} feature Feature to remove
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    removeFeature(feature: Feature, refresh = true): void  {
        const layer = this.getRenderLayerByID(feature.ID);
        if (this.ensureFeatureExists(feature)) {
            this.view.removeFeature(feature);
            this.refresh(refresh);
        }
        layer.removeFeatureByID(feature.ID);
    }

    /**
     * Removes feature from the view
     * @param {string} feature Feature to remove
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    removeFeatureByID(featureID: string, refresh = true): void  {
        const layer = this.getRenderLayerByID(featureID);
        const feature = layer.getFeature(featureID);
        if (this.ensureFeatureExists(feature)) {
            this.view.removeFeature(feature);
            this.refresh(refresh);
        }
        layer.removeFeatureByID(featureID);
    }

    /**
     * Adds layer to the view
     * @param {Layer} layer Layer to add
     * @param {Number} index Index of the layer
     * @param {Boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    addLayer(layer: Layer, index: number, refresh = true): void  {
        if (this.__isLayerInCurrentDevice(layer)) {
            this.view.addLayer(layer, index);
            this.__addAllLayerFeatures(layer, index, false);
            this.refresh(refresh);
        }
    }

    /**
     * Create a new set of layers (flow, control and cell) for the upcoming level.
     * @returns {void}
     * @memberof ViewManager
     */
    createNewLayerBlock(): void  {
        if(this.__currentDevice === null) {
            throw new Error("No device set on ViewManager");
        }
        // Generate model layers
        let groupNum = this.__currentDevice.layers.length;
        if (groupNum != 0) groupNum = groupNum / 3;

        const newlayers = [];
        newlayers[0] = new Layer({ z_offset: 0, flip: false }, this.currentDevice?.generateNewName("LayerFlow"), LogicalLayerType.FLOW, groupNum.toString());
        newlayers[1] = new Layer({ z_offset: 0, flip: false }, this.currentDevice?.generateNewName("LayerControl"), LogicalLayerType.CONTROL, groupNum.toString());
        newlayers[2] = new Layer({ z_offset: 0, flip: false }, this.currentDevice?.generateNewName("LayerIntegration"), LogicalLayerType.INTEGRATION, groupNum.toString());
        // Add model layers to current device
        this.__currentDevice.createNewLayerBlock(newlayers);

        // Find all the edge features
        const edgefeatures = [];
        const devicefeatures = Registry.currentDevice?.layers[0].features;
        let feature;

        for (const i in devicefeatures) {
            feature = devicefeatures[i];
            if (feature.fabType === "EDGE") {
                edgefeatures.push(feature);
            }
        }

        // Add the Edge Features from layer '0'
        // to all other layers
        for (const i in newlayers) {
            for (const j in edgefeatures) {
                newlayers[i].addFeature(edgefeatures[j]);
            }
        }

        // Added the new layers
        for (const i in newlayers) {
            const layertoadd = newlayers[i];
            const index = this.view.paperLayers.length;
            this.addLayer(layertoadd, index, true);
        }

        // Add new renderLayers
        this.renderLayers[this.renderLayers.length] = new RenderLayer(this.currentDevice?.generateNewName("RenderLayerFlow"), newlayers[0], LogicalLayerType.FLOW);
        this.renderLayers[this.renderLayers.length] = new RenderLayer(this.currentDevice?.generateNewName("RenderLayerControl"), newlayers[1], LogicalLayerType.CONTROL);
        this.renderLayers[this.renderLayers.length] = new RenderLayer(this.currentDevice?.generateNewName("RenderLayerIntegration"), newlayers[2], LogicalLayerType.INTEGRATION);
        for (const i in edgefeatures) {
            this.renderLayers[this.renderLayers.length - 3].addFeature(edgefeatures[i]);
            this.renderLayers[this.renderLayers.length - 2].addFeature(edgefeatures[i]);
            this.renderLayers[this.renderLayers.length - 1].addFeature(edgefeatures[i]);
        }
        
        console.log("Active Layer", this.activeRenderLayerIndex);
        this.setActiveRenderLayer(this.renderLayers.length - 3);
    }

    /**
     * Deletes the layers at the level index, we have 3-set of layers so it deletes everything at
     * that level
     * @param {number} levelindex Integer only
     * @returns {void}
     * @memberof ViewManager
     */
    deleteLayerBlock(levelindex: number): void  {
        // Delete the levels in the device model
        Registry.currentDevice?.deleteLayer(levelindex * 3);
        Registry.currentDevice?.deleteLayer(levelindex * 3);
        Registry.currentDevice?.deleteLayer(levelindex * 3);

        // Delete levels in render model
        this.renderLayers.splice(levelindex * 3, 3);
        if (this.activeRenderLayerIndex! > levelindex * 3 + 2) {
            this.setActiveRenderLayer(this.activeRenderLayerIndex! - 3);
        } else if (this.activeRenderLayerIndex! < levelindex * 3) {
            console.log("No change");
        } else {
            if (levelindex == 0) {
                if (this.renderLayers.length == 0) {
                    // The param shouldn't be null
                    this.setActiveRenderLayer(levelindex);
                } else {
                    this.setActiveRenderLayer(0);
                }
            } else {
                this.setActiveRenderLayer((levelindex - 1) * 3);
            }
        }

        // Delete the levels in the render model
        this.view.removeLayer(levelindex * 3);
        this.view.removeLayer(levelindex * 3);
        this.view.removeLayer(levelindex * 3);
        this.updateActiveLayer();
        this.refresh();
    }

    setActiveRenderLayer(index: number): void  {
        this.activeRenderLayerIndex = index;
        this.currentLayer = this.renderLayers[index]; // Registry.currentDevice.layers[index];
        this.updateActiveLayer();
        this.refreshValveCrossLayerOpacity();
        EventBus.get().emit(EventBus.ACTIVE_RENDER_LAYER_CHANGED);
        if (this.portsOnlyView) {
            this.view.applyPortsOnlyVisibility(true);
        }
    }

    /**
     * Toggle the cover-layer preview: only FLOW (blue) and CONTROL (red) ports.
     */
    setPortsOnlyView(enabled: boolean): void {
        this.portsOnlyView = Boolean(enabled);
        this.view.applyPortsOnlyVisibility(this.portsOnlyView);
        this.refresh(true);
        EventBus.get().emit(EventBus.PORTS_ONLY_VIEW_CHANGED, this.portsOnlyView);
    }

    private resetPortsOnlyView(): void {
        this.portsOnlyView = false;
        EventBus.get().emit(EventBus.PORTS_ONLY_VIEW_CHANGED, false);
    }

    /**
     * Re-render valve features so FLOW vs CONTROL opacity matches the active logical layer tab.
     */
    private refreshValveCrossLayerOpacity(): void {
        const device = this.currentDevice;
        if (!device) return;
        let updated = false;
        for (const f of device.getAllFeaturesFromDevice()) {
            const t = f.getType();
            if (t !== "Valve" && t !== "Valve3D_control" && t !== "Valve3D") continue;
            if (!this.ensureFeatureExists(f)) continue;
            this.view.updateFeature(f);
            updated = true;
        }
        if (updated) this.refresh(false);
    }

    /**
     * Removes layer from the view
     * @param {Layer} layer Layer to be removed from the view
     * @param {Number} index Index of the layer to remove
     * @param {Boolean} refresh Default to true
     * @returns {view}
     * @memberof ViewManager
     */
    removeLayer(layer: Layer, index: number, refresh = true): void  {
        if (this.__isLayerInCurrentDevice(layer)) {
            this.view.removeLayer(index);
            this.__removeAllLayerFeatures(layer);
            this.refresh(refresh);
        }
    }

    /**
     * Converts the layers to SVG format
     * @returns {}
     * @memberof ViewManager
     */
    layersToSVGStrings() {
        return this.view.layersToSVGStrings();
    }

    exportSVGString(portsOnly = false): string | null {
        return this.view.captureSVGString(portsOnly);
    }

    /**
     * Adds all the features of the layer
     * @param {Layer} layer Selected layer
     * @param {Boolean} refresh Default to true
     * @returns {void}
     * @memberof ViewManager
     * @private
     */
    __addAllLayerFeatures(layer: Layer, index: number, refresh = true): void  {
        for (const key in layer.features) {
            const feature = layer.features[key];
            this.addFeature(feature, index, false);
            this.refresh(refresh);
        }
    }

    /**
     * Updates all the feature of the layer
     * @param {Layer} layer Selected layer
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    __updateAllLayerFeatures(layer: Layer, refresh = true): void  {
        for (const key in layer.features) {
            const feature = layer.features[key];
            this.updateFeature(feature, false);
            this.refresh(refresh);
        }
    }

    /**
     * Removes all feature of the layer
     * @param {Layer} layer Selected layer
     * @param {Boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    __removeAllLayerFeatures(layer: Layer, refresh = true): void  {
        for (const key in layer.features) {
            const feature = layer.features[key];
            this.removeFeature(feature, false);
            this.refresh(refresh);
        }
    }

    /**
     * Updates layer
     * @param {Layer} layer Selected layer to be updated
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    updateLayer(layer: Layer, refresh = true): void  {
        if (this.__isLayerInCurrentDevice(layer)) {
            // Missing param
            this.refresh(refresh);
        }
    }

    /**
     * Updates the active layer
     * @param {Boolean} refresh Default to true
     * @returns {void}
     * @memberof ViewManager
     */
    updateActiveLayer(refresh = true): void  {
        if(this.activeRenderLayerIndex === null){
            console.warn("Attempting update active layer in view manager with no active layer value");
            return;
        }
        this.view.setActiveLayer(this.activeRenderLayerIndex as number);
        this.refresh(refresh);
    }

    /**
     * Removes the grid
     * @param {Boolean} refresh Default to true
     * @returns {void}
     * @memberof ViewManager
     */
    removeGrid(refresh = true): void  {
        if (this.__hasCurrentGrid()) {
            this.view.removeGrid();
            this.refresh(refresh);
        }
    }

    /**
     * Update grid
     * @param {Boolean} refresh Default to true
     * @returns {void}
     * @memberof ViewManager
     */
    updateGrid(refresh = true): void  {
        if (this.__hasCurrentGrid()) {
            this.view.updateGrid(Registry.currentGrid);
            this.refresh(refresh);
            // Send the EventBus Notification
            EventBus.get().emit(EventBus.UPDATE_GRID_SIZE);
        }
    }

    /**
     * Update the alignment marks of the view
     * @returns {void}
     * @memberof ViewManager
     */
    updateAlignmentMarks(): void  {
        this.view.updateAlignmentMarks();
    }

    /**
     * Clear the view
     * @returns {void}
     * @memberof ViewManager
     */
    clear(): void  {
        this.view.clear();
    }

    /**
     * Sets a specific value of zoom
     * @param {Number} zoom Zoom value
     * @param {boolean} refresh Whether it will refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    setZoom(zoom: number, refresh = true): void  {
        if (zoom > this.maxZoom) zoom = this.maxZoom;
        else if (zoom < this.minZoom) zoom = this.minZoom;
        this.view.setZoom(zoom);
        this.updateGrid(false);
        this.updateAlignmentMarks();
        this.view.updateRatsNest();
        this.view.updateComponentPortsRender();

        this.updateDevice(Registry.currentDevice!, false);
        this.__updateViewTarget(false);
        this.refresh(refresh);

    }

    /**
     * Automatically generates a rectangular border for the device
     * @returns {void}
     * @memberof ViewManager
     */
    generateBorder(): void  {
        const borderfeature = new EdgeFeature(null, new Params({}, new Map(), new Map()));

        // Get the bounds for the border feature and then update the device dimensions
        const xspan = Registry.currentDevice?.getXSpan();
        const yspan = Registry.currentDevice?.getYSpan();
        borderfeature.generateRectEdge(xspan as number, yspan as number);

        // Adding the feature to all the layers
        for (const i in Registry.currentDevice!.layers) {
            const layer = Registry.currentDevice!.layers[i];
            layer.addFeature(borderfeature);
        }
    }

    /**
     * Accepts a DXF object and then converts it into a feature, an edgeFeature in particular
     * @param dxfobject
     * @returns {void}
     * @memberof ViewManager
     */
    importBorder(dxfobject: any): void  {
        const customborderfeature = new EdgeFeature(null, new Params({}, new Map(), new Map()));
        for (const i in dxfobject.entities) {
            const foo = new DXFObject(dxfobject.entities[i]);
            customborderfeature.addDXFObject(foo);
        }

        // Adding the feature to all the layers
        if(Registry.currentDevice === null) {
            throw new Error("No device selected");
        }
        for (const i in Registry.currentDevice.layers) {
            const layer = Registry.currentDevice.layers[i];
            layer.addFeature(customborderfeature);
        }

        // Get the bounds for the border feature and then update the device dimensions
        const bounds = this.view.getRenderedFeature(customborderfeature.ID).bounds;

        Registry.currentDevice?.setXSpan(bounds.width);
        Registry.currentDevice?.setYSpan(bounds.height);
        // Refresh the view
        Registry.viewManager?.view.initializeView();
        Registry.viewManager?.view.refresh();
    }

    /**
     * Deletes the border
     * @returns {void}
     * @memberof ViewManager
     */
    deleteBorder(): void  {
        /*
        1. Find all the features that are EDGE type
        2. Delete all these features
         */

        console.log("Deleting border...");

        const features = Registry.currentDevice?.getAllFeaturesFromDevice();
        console.log("All features", features);

        const edgefeatures = [];
        if (features === undefined){
            throw new Error("No features found");
        }
        for (const i in features) {
            // Check if the feature is EDGE or not
            if (features[i].fabType === "EDGE") {
                edgefeatures.push(features[i]);
            }
        }

        // Delete all the features
        for (const i in edgefeatures) {
            this.removeFeatureByID(edgefeatures[i].ID);
        }

        console.log("Edgefeatures", edgefeatures);
    }

    /**
     * Removes the placement ghost and clears lastTarget* so zoom cannot
     * redraw a stale semi-transparent component after leaving placement.
     * @memberof ViewManager
     * @returns {void}
     */
    removeTarget(): void  {
        this.view.clearTargetState();
    }

    /**
     * Update the target view
     * @param {string} featureType
     * @param {string} featureSet
     * @param {Array<number>} position Array with X and Y coordinates
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    updateTarget(featureType: string, featureSet: string, position: Point, currentParameters: { [k: string]: any } | null = null, refresh = true): void  {
        this.view.addTarget(featureType, featureSet, position, currentParameters);
        this.view.updateAlignmentMarks();
        this.view.updateRatsNest();
        this.refresh(refresh);
    }

    /**
     * Update the view target
     * @param {Boolean} refresh Default to true
     * @returns {void}
     * @memberof ViewManager
     */
    __updateViewTarget(refresh = true): void  {
        this.view.updateTarget();
        this.updateAlignmentMarks();
        this.view.updateRatsNest();
        this.view.updateComponentPortsRender();
        this.refresh(refresh);
    }

    /**
     * Adjust the zoom value in a certain point
     * @param {Number} delta Value of zoom
     * @param {Array<number>} point Coordinates to zoom in
     * @param {Boolean} refresh Default to true
     * @returns {void}
     * @memberof ViewManager
     */
    adjustZoom(delta: number, point: number[], refresh = true): void  {
        const belowMin = this.view.getZoom() >= this.maxZoom && delta < 0;
        const aboveMax = this.view.getZoom() <= this.minZoom && delta > 0;
        if (!aboveMax && !belowMin) {
            this.view.adjustZoom(delta, new paper.Point(point));
            this.updateGrid(false);
            // this.updateAlignmentMarks();
            this.view.updateRatsNest();
            this.view.updateComponentPortsRender();
            this.updateDevice(Registry.currentDevice!, false);
            this.__updateViewTarget(false);
        } else {
            // console.log("Too big or too small!");
        }
        this.refresh(refresh);
    }

    /**
     * Sets the center value
     * @param {Array<number>} center Center coordinates
     * @param {Boolean} refresh Default to true
     * @returns {void}
     * @memberof ViewManager
     */
    setCenter(center: number[], refresh = true): void  {
        this.view.setCenter((center as unknown) as paper.Point);
        this.updateGrid(false);
        // this.updateAlighmentMarks();

        this.updateDevice(Registry.currentDevice!, false);
        this.refresh(refresh);
    }

    /**
     * Moves center by a certain value
     * @param {number} delta
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    moveCenter(delta: paper.Point, refresh = true): void  {
        this.view.moveCenter(delta);
        this.updateGrid(false);
        // this.updateAlignmentMarks();
        this.view.updateRatsNest();
        this.view.updateComponentPortsRender();
        this.updateDevice(Registry.currentDevice!, false);
        this.refresh(refresh);
    }

    /**
     * Save the device to JSON format
     * @returns {void}
     * @memberof ViewManager
     */
    saveToStorage(): void  {
        if (Registry.viewManager) {
            try {
                localStorage.setItem("currentDevice", JSON.stringify(Registry.viewManager.generateExportJSON()));
            } catch (err) {
                // can't save, so.. don't?
            }
        }
    }

    /**
     * Refresh the view
     * @param {boolean} refresh Whether to refresh or not. true by default
     * @returns {void}
     * @memberof ViewManager
     */
    refresh(refresh = true): void  {
        this.updateQueue.run();
        // Update the toolbar
        const spacing = Registry.currentGrid?.getSpacing();
        // TODO - undo this
        // this.resolutionToolBar.updateResolutionLabelAndSlider(spacing);
    }

    /**
     * Gets the coordinates of the project
     * @param {*} event
     * @returns {Array<number>} Returns the X and Y coordinates
     * @memberof ViewManager
     */
    getEventPosition(event: any) {
        return this.view.getProjectPosition(event.clientX, event.clientY);
    }

    /**
     * Checks if it has current grid
     * @returns {Boolean}
     * @memberof ViewManager
     */
    __hasCurrentGrid(): boolean  {
        if (Registry.currentGrid) return true;
        else return false;
    }

    /**
     * Checks if layer is in the current device
     * @param {Layer} layer Layer to check if it's on the current device
     * @returns {Boolean}
     * @memberof ViewManager
     */
    __isLayerInCurrentDevice(layer: Layer): boolean  {
        if (Registry.currentDevice && layer.device === Registry.currentDevice) return true;
        else return false;
    }

    /**
     * Checks if feature is in the current device
     * @param {Object} feature Feature to check if it's on the current device
     * @returns {Boolean}
     * @memberof ViewManager
     */
    isFeatureInCurrentDevice(feature: { [k: string]: any }): boolean  {
        if (Registry.currentDevice && this.__isLayerInCurrentDevice(feature.layer)) return true;
        else return false;
    }

    /**
     * Checks if feature exists
     * @param {Feature} feature Feature to check whether in existence
     * @returns {Boolean}
     * @memberof ViewManager
     */
    ensureFeatureExists(feature: Feature): boolean  {
        for (let i = 0; i < this.renderLayers.length; i++) {
            if (this.renderLayers[i].containsFeature(feature)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Loads a device from a JSON format
     * @param {JSON} json
     * @returns {void}
     * @memberof ViewManager
     */
    loadDeviceFromJSON(json: InterchangeV1_2): void  {
        // Uploading a design is a canvas refresh: same as Escape (exit
        // placement/move ghosts, selection, and dialogs) so wheel/zoom
        // cannot resurrect the last component.
        try {
            exitCanvasSettingsLikeEscape();
        } catch {
            try {
                this.deactivateComponentPlacementTool();
            } catch {
                this.resetToDefaultTool();
            }
            try {
                if (this.tools && this.tools.MoveTool) {
                    this.tools.MoveTool.deactivate();
                }
            } catch {
                /* MoveTool may be unset during early init */
            }
            this.removeTarget();
            try {
                this.view.clearSelectedItems();
            } catch {
                /* view may not be ready */
            }
        }
        this.importedSourceJson = cloneImportedDeviceJson(json);
        let device;
        this.resetPortsOnlyView();
        Registry.viewManager?.clear();
        this.renderLayers = [];
        this.activeRenderLayerIndex = 0;
        this.__currentDevice = null;
        // Check and see the version number if its 0 or none is present,
        // its going the be the legacy format, else it'll be a new format
        const version = json.version;

        let flag = false;
        let i = 1;

        if (!json.params || typeof json.params !== "object") {
            json.params = { width: 135000, length: 85000 };
        }

        const spanWidth = json.params["x-span"] != null ? json.params["x-span"] : json.params.width;
        const spanLength = json.params["y-span"] != null ? json.params["y-span"] : json.params.length;
        // Prefer Parchmint x-span/y-span after merge so stale width/length cannot win.
        json.params = {
            ...json.params,
            width: spanWidth != null ? spanWidth : 135000,
            length: spanLength != null ? spanLength : 85000,
        };
        delete json.params["x-span"];
        delete json.params["y-span"];

        if (!Array.isArray(json.components)) {
            json.components = [];
        }

            // JSON params.position is the rotation / geometric center for every
            // primitive (PR export and 3DuF hand placement). render2D converts
            // that center to the library draw origin at draw time.
            for (const component of json.components) {
                const MintType = String(ComponentAPI.getTypeForMINT(component.entity, component.params));
                if (MintType === "Node") {
                    component["x-span"] = 0;
                    component["y-span"] = 0;
                }
            }

            requestAnimationFrame(() => {
                this.view.updateComponentPortsRender();
            });


        if (version === null || undefined === version || version === "1" || version == "1.1" || version == "1.2") {
            

            const ret = LoadUtils.loadFromScratch(json);
            device = ret[0];
            Registry.currentDevice = device;
            this.__currentDevice = device;
            device.syncComponentPositionsToFeatures();

            this.renderLayers = ret[1];

            this.setNameMap();
            } else if (version == "1.1") {
                // this.loadCustomComponents(json);
                device = Device.fromInterchangeV1_1(json);
                Registry.currentDevice = device;
                this.__currentDevice = device;
                device.syncComponentPositionsToFeatures();

                // TODO: Add separate render layers to initializing json, make fromInterchangeV1_1???
                for (const i in json.layers) {
                    const newRenderLayer = RenderLayer.fromInterchangeV1(json.renderLayers[i]);
                    this.renderLayers.push(newRenderLayer);
                }
        } else {
            alert("Version '" + version + "' is not supported by 3DuF !");
        }
        // Common Code for rendering stuff
        // console.log("Feature Layers", Registry.currentDevice.layers);
        this.currentLayer = this.renderLayers[0];
        // Registry.currentTextLayer = Registry.currentDevice!.textLayers[0];

        this.activeRenderLayerIndex = 0;

        // TODO: Need to replace the need for this function, right now without this, the active layer system gets broken
        this.addDevice(Registry.currentDevice!);

        // In case of MINT exported json, generate layouts for rats nests
        this.__initializeRatsNest();

        // Do not rewrite imported waypoints onto library ports. TREE-PLACE
        // wayPoints are already absolute; snapping them to getPorts() (often
        // a different local frame than the Parchmint AABB ports) pulled
        // channels off mixers / valves / mux arms.

        // Re-apply valve-induced connection breaks after JSON load so imported designs
        // render the same valve gap geometry as interactive placement.
        this.reapplyValveConnectionBreaks();

        this.logImportedDeviceDiagnostics(json);

        this.view.initializeView();
        this.updateGrid();
        this.updateDevice(Registry.currentDevice!);
        this.refresh(true);
        this.currentLayer = this.renderLayers[0];
        // this.layerToolBar.setActiveLayer("0");
        this.updateActiveLayer();

        if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
            window.dispatchEvent(new CustomEvent("threeduf-device-loaded"));
        }


        console.log(json.version);
        //If older version fix feature locations
        if (this.__currentDevice !== null) {
            console.log("version: ", json.version);
            if (json.version == "1" || json.version == "1.1") {
                for (const i in this.__currentDevice.components) {
                    //[center[0] - (center[0] - rect.x), center[1] - (center[1] - rect.y)]
                    const rect = this.__currentDevice.components[i].getBoundingRectangle();
                    const currPos = this.__currentDevice.components[i].getPosition();
                    this.__currentDevice.components[i].updateComponentPosition([currPos[0] + (currPos[0] - rect.x), currPos[1] + (currPos[1] - rect.y)]);
                }
            }
        }
    }

    /**
     * Pull imported connection terminals onto library port positions so the
     * drawn channel meets the primitive after rotation-center load.
     */
    private snapImportedConnectionTerminals(): void {
        const device = this.currentDevice;
        if (!device) {
            return;
        }
        for (const connection of device.connections) {
            try {
                connection.snapImportedTerminals();
            } catch (err) {
                console.warn("Could not snap connection terminals during JSON load:", connection.id, err);
            }
        }
    }

    /**
     * Applies connection segment breaks for every connection that has mapped valves.
     * This keeps imported JSON behavior consistent with interactive valve insertion.
     */
    private reapplyValveConnectionBreaks(): void {
        const device = this.currentDevice;
        if (!device) {
            return;
        }
        this.ensureValveFlowFeatures();
        this.ensureValveControlFeatures();
        this.ensureValveComponentsFromLooseFeatures();
        for (const connection of device.connections) {
            try {
                this.updatesConnectionRender(connection);
            } catch (err) {
                console.warn("Could not re-apply valve gap for connection during JSON load:", connection.id, err);
            }
        }
    }

    /**
     * Dump a compact load report so mux / valve / missing-channel bugs show up
     * in the browser console (copy from DevTools → Console).
     */
    private logImportedDeviceDiagnostics(json: { name?: string; components?: any[]; connections?: any[] }): void {
        const device = this.currentDevice;
        const prefix = "[3DuF import]";
        if (!device) {
            console.error(prefix, "no device after load");
            return;
        }
        const connections = device.connections || [];
        const components = device.components || [];
        const unrouted = connections.filter(c => !c.routed).map(c => c.id || c.name);
        const byEntity: { [k: string]: number } = {};
        for (const c of components) {
            const ent = String(c.mint || "UNKNOWN");
            byEntity[ent] = (byEntity[ent] || 0) + 1;
        }
        const missingFeatures: string[] = [];
        const connectionSummary = connections.map(conn => {
            let start: unknown = null;
            let end: unknown = null;
            let nseg = 0;
            try {
                const wps = conn.getValue("wayPoints");
                if (Array.isArray(wps) && wps.length) {
                    start = wps[0];
                    end = wps[wps.length - 1];
                }
            } catch {
                /* wayPoints missing */
            }
            try {
                const segs = conn.getValue("segments");
                nseg = Array.isArray(segs) ? segs.length : 0;
            } catch {
                nseg = 0;
            }
            const nfeat = conn.featureIDs ? conn.featureIDs.length : 0;
            if (nfeat === 0 || nseg === 0) {
                missingFeatures.push(String(conn.id || conn.name));
            }
            const src = conn.source ? conn.source.component?.id || conn.source.component?.name : null;
            const sinks = (conn.sinks || []).map(s => s.component?.id || s.component?.name);
            return {
                id: conn.id,
                name: conn.name,
                layer: conn.layer?.id,
                routed: conn.routed,
                features: nfeat,
                segments: nseg,
                start,
                end,
                source: src,
                sinks
            };
        });
        console.groupCollapsed(`${prefix} ${json.name || device.name}`);
        console.log("components by entity", byEntity);
        console.log("connections", connectionSummary);
        if (unrouted.length) {
            console.warn(prefix, "unrouted connections (will show as rats-nest, not a solid pipe):", unrouted);
        }
        if (missingFeatures.length) {
            console.error(prefix, "connections with no feature or no segments (pipe will not render):", missingFeatures);
        }
        for (const c of components) {
            const ent = String(c.mint || "").toUpperCase();
            if (ent !== "PORT" && ent !== "VALVE3D") {
                continue;
            }
            let pos: number[] | null = null;
            try {
                pos = c.getValue("position");
            } catch {
                pos = null;
            }
            const hits: string[] = [];
            if (pos && pos.length >= 2) {
                for (const conn of connections) {
                    let wps: number[][] = [];
                    try {
                        wps = conn.getValue("wayPoints") || [];
                    } catch {
                        wps = [];
                    }
                    for (const wp of wps) {
                        if (
                            Array.isArray(wp) &&
                            Math.abs(Number(wp[0]) - Number(pos[0])) < 1 &&
                            Math.abs(Number(wp[1]) - Number(pos[1])) < 1
                        ) {
                            hits.push(String(conn.id || conn.name));
                            break;
                        }
                    }
                }
            }
            if (!hits.length && ent === "PORT") {
                console.warn(
                    prefix,
                    "PORT position does not sit on any connection waypoint (looks disconnected):",
                    c.id || c.name,
                    pos
                );
            }
        }
        console.groupEnd();
    }

    /**
     * Backward-compatibility shim for imported designs that only store CONTROL-side Valve3D
     * features. If a VALVE3D component has no FLOW feature, synthesize one on the matching FLOW
     * layer so loaded designs render the same crescent-with-gap valve body as interactive placement.
     */
    private ensureValveFlowFeatures(): void {
        const device = this.currentDevice;
        if (!device) {
            return;
        }
        for (const component of device.components) {
            if (component.mint !== "VALVE3D") {
                continue;
            }
            const componentFeatures: Feature[] = [];
            for (const featureID of component.featureIDs) {
                try {
                    componentFeatures.push(device.getFeatureByID(featureID));
                } catch {
                    continue;
                }
            }
            const hasFlowValveFeature = componentFeatures.some(
                feature => feature.getType() === "Valve3D" && feature.layer?.type === LogicalLayerType.FLOW
            );
            if (hasFlowValveFeature) {
                continue;
            }
            const sourceFeature = componentFeatures.find(
                feature =>
                    (feature.getType() === "Valve3D_control" || feature.getType() === "Valve3D") &&
                    feature.layer !== null
            );
            if (!sourceFeature || !sourceFeature.layer) {
                continue;
            }
            const targetFlowRenderLayer = this.renderLayers.find(
                layer =>
                    layer.type === LogicalLayerType.FLOW &&
                    layer.physicalLayer !== null &&
                    layer.physicalLayer.group === sourceFeature.layer!.group
            );
            if (!targetFlowRenderLayer) {
                continue;
            }
            const params = {
                position: sourceFeature.getValue("position"),
                rotation: sourceFeature.getValue("rotation"),
                componentSpacing: sourceFeature.getValue("componentSpacing"),
                valveRadius: sourceFeature.getValue("valveRadius"),
                depth: sourceFeature.getValue("depth"),
                gap: sourceFeature.getValue("gap"),
                width: sourceFeature.getValue("width"),
                length: sourceFeature.getValue("length")
            };
            const flowFeature = Device.makeFeature("Valve3D", params);
            flowFeature.referenceID = component.id;
            const targetIndex = this.renderLayers.indexOf(targetFlowRenderLayer);
            if (targetIndex >= 0) {
                this.addFeature(flowFeature, targetIndex, true, false);
                component.addFeatureID(flowFeature.ID);
            }
        }
    }

    /**
     * Ensure every FLOW-side Valve3D has a matching CONTROL-side Valve3D_control so the full red
     * circular control geometry is visible when switching to CONTROL layers (including legacy files
     * that only contain standalone FLOW valve features).
     */
    private ensureValveControlFeatures(): void {
        const device = this.currentDevice;
        if (!device) {
            return;
        }

        for (const flowLayer of device.layers) {
            if (flowLayer.type !== LogicalLayerType.FLOW) {
                continue;
            }
            const controlLayer = device.layers.find(
                layer => layer.type === LogicalLayerType.CONTROL && layer.group === flowLayer.group
            );
            if (!controlLayer) {
                continue;
            }
            const targetControlRenderLayer = this.renderLayers.find(
                layer =>
                    layer.type === LogicalLayerType.CONTROL &&
                    layer.physicalLayer !== null &&
                    layer.physicalLayer.id === controlLayer.id
            );
            if (!targetControlRenderLayer) {
                continue;
            }

            const flowFeatures = flowLayer.getAllFeaturesFromLayer();
            for (const featureID in flowFeatures) {
                const feature = flowFeatures[featureID];
                if (feature.getType() !== "Valve3D") {
                    continue;
                }

                const position = feature.getValue("position");
                const rotation = Number(feature.getValue("rotation"));
                const referenceID = feature.referenceID;

                const alreadyHasControlCounterpart = Object.values(controlLayer.getAllFeaturesFromLayer()).some(ctrlFeature => {
                    if (ctrlFeature.getType() !== "Valve3D_control") {
                        return false;
                    }
                    if (referenceID && ctrlFeature.referenceID === referenceID) {
                        return true;
                    }
                    const ctrlPos = ctrlFeature.getValue("position");
                    const ctrlRot = Number(ctrlFeature.getValue("rotation"));
                    return (
                        ctrlPos &&
                        ctrlPos[0] === position[0] &&
                        ctrlPos[1] === position[1] &&
                        ctrlRot === rotation
                    );
                });

                if (alreadyHasControlCounterpart) {
                    continue;
                }

                const params = {
                    position: feature.getValue("position"),
                    rotation: feature.getValue("rotation"),
                    componentSpacing: feature.getValue("componentSpacing"),
                    valveRadius: feature.getValue("valveRadius"),
                    depth: feature.getValue("depth"),
                    gap: feature.getValue("gap"),
                    width: feature.getValue("width"),
                    length: feature.getValue("length")
                };
                const controlFeature = Device.makeFeature("Valve3D_control", params);
                controlFeature.referenceID = referenceID;
                const targetIndex = this.renderLayers.indexOf(targetControlRenderLayer);
                if (targetIndex >= 0) {
                    this.addFeature(controlFeature, targetIndex, true, false);
                    if (referenceID) {
                        const component = device.getComponentByID(referenceID);
                        if (component) {
                            component.addFeatureID(controlFeature.ID);
                        }
                    }
                }
            }
        }
    }

    private findNearestConnectionForValvePoint(position: Point, group: string): Connection | null {
        if (!this.currentDevice) {
            return null;
        }
        const target = new paper.Point(position[0], position[1]);
        let best: Connection | null = null;
        let bestDist = Number.POSITIVE_INFINITY;
        for (const connection of this.currentDevice.connections) {
            if (!connection.layer || connection.layer.group !== group) {
                continue;
            }
            if (connection.layer.type === LogicalLayerType.CONTROL) {
                continue;
            }
            const segments = connection.getValue("segments");
            if (!Array.isArray(segments)) {
                continue;
            }
            for (const segment of segments) {
                const line = new paper.Path.Line(
                    new paper.Point(segment[0][0], segment[0][1]),
                    new paper.Point(segment[1][0], segment[1][1])
                );
                const nearest = line.getNearestPoint(target);
                const dist = nearest.getDistance(target);
                line.remove();
                if (dist < bestDist) {
                    bestDist = dist;
                    best = connection;
                }
            }
        }
        return best;
    }

    /**
     * Normalize legacy standalone Valve3D features (referenceID=null) into real component objects
     * so selection/double-click/settings behavior matches regular components.
     */
    private ensureValveComponentsFromLooseFeatures(): void {
        const device = this.currentDevice;
        if (!device) {
            return;
        }
        for (const flowLayer of device.layers) {
            if (flowLayer.type !== LogicalLayerType.FLOW) {
                continue;
            }
            const controlLayer = device.layers.find(
                layer => layer.type === LogicalLayerType.CONTROL && layer.group === flowLayer.group
            );
            const flowFeatures = flowLayer.getAllFeaturesFromLayer();
            for (const featureID in flowFeatures) {
                const flowFeature = flowFeatures[featureID];
                if (flowFeature.getType() !== "Valve3D" || flowFeature.referenceID !== null) {
                    continue;
                }

                const flowPos = flowFeature.getValue("position");
                const flowRot = Number(flowFeature.getValue("rotation"));
                let controlFeature: Feature | null = null;
                if (controlLayer) {
                    for (const ctrlID in controlLayer.getAllFeaturesFromLayer()) {
                        const candidate = controlLayer.getAllFeaturesFromLayer()[ctrlID];
                        if (candidate.getType() !== "Valve3D_control" || candidate.referenceID !== null) {
                            continue;
                        }
                        const ctrlPos = candidate.getValue("position");
                        const ctrlRot = Number(candidate.getValue("rotation"));
                        if (
                            ctrlPos &&
                            ctrlPos[0] === flowPos[0] &&
                            ctrlPos[1] === flowPos[1] &&
                            ctrlRot === flowRot
                        ) {
                            controlFeature = candidate;
                            break;
                        }
                    }
                }

                const definition = ComponentAPI.getDefinition("Valve3D_control") || ComponentAPI.getDefinition("Valve3D");
                if (!definition) {
                    continue;
                }
                const cleanParamData: { [k: string]: any } = {};
                const params = flowFeature.getParams();
                for (const key in params) {
                    cleanParamData[key] = params[key].value;
                }
                cleanParamData.position = [0, 0];
                const componentParams = new Params(
                    cleanParamData,
                    MapUtils.toMap(definition.unique),
                    MapUtils.toMap(definition.heritable)
                );
                const componentID = ComponentAPI.generateID();
                const valveComponent = new Component(componentParams, device.generateNewName("Valve3D"), definition.mint, componentID);

                valveComponent.addFeatureID(flowFeature.ID);
                flowFeature.referenceID = componentID;
                if (controlFeature) {
                    valveComponent.addFeatureID(controlFeature.ID);
                    controlFeature.referenceID = componentID;
                }
                valveComponent.setInitialOffset();
                valveComponent.updateComponentPosition(flowPos);
                device.addComponent(valveComponent);

                const targetConnection = this.findNearestConnectionForValvePoint(flowPos, flowLayer.group);
                if (targetConnection) {
                    device.insertValve(valveComponent, targetConnection, ValveType.NORMALLY_CLOSED);
                }
            }
        }
    }

    /**
     * Removes the features of the current device by searching on it's ID
     * @param {*} paperElements
     * @returns {void}
     * @memberof ViewManager
     */
    removeFeaturesByPaperElements(paperElements: any): void  {
        if (paperElements.length > 0) {
            for (let i = 0; i < paperElements.length; i++) {
                const paperFeature = paperElements[i];
                this.removeFeatureByID(paperFeature.featureID);
            }
            this.currentSelection = [];
        }
    }

    /**
     * Updates the component parameters of a specific component
     * @param {string} componentname
     * @param {Array} params
     * @returns {void}
     * @memberof ViewManager
     */
    updateComponentParameters(componentname: string, params: { [k: string]: any }): void  {
        const component = this.__currentDevice?.getComponentByName(componentname);
        for (const key in params) {
            component?.updateParameter(key, params[key]);
        }
    }

    /**
     * Returns a Point, coordinate list that is the closes grid coordinate
     * @param {Array<number>} point Array with the X and Y coordinates
     * @return {void|Array<number>}
     * @memberof ViewManager
     */
    snapToGrid(point: Point): Point {
        if (Registry.currentGrid)
        { 
            const closestpt = Registry.currentGrid.getClosestGridPoint(new paper.Point(point[0], point[1]));
            return [closestpt.x, closestpt.y];
        } else { return point; }
    }

    /**
     * Gets the features of a specific type ?
     * @param {string} typeString
     * @param {string} setString
     * @param {Array} features Array with features
     * @returns {Array} Returns array with the features of a specific type
     * @memberof ViewManager
     */
    getFeaturesOfType(typeString: string, features: Feature[]) {
        const output = [];
        for (let i = 0; i < features.length; i++) {
            const feature = features[i];
            if (feature.getType() === typeString) {
                output.push(feature);
            }
        }
        return output;
    }

    /**
     * Updates all feature parameters
     * @param {string} valueString
     * @param {*} value
     * @param {Array} features Array of features
     * @returns {void}
     * @memberof ViewManager
     */
    adjustAllFeatureParams(valueString: string, value: any, features: Feature[]): void  {
        for (let i = 0; i < features.length; i++) {
            const feature = features[i];
            feature.updateParameter(valueString, value);
        }
    }

    /**
     * Adjust all parameters of the same type
     * @param {string} typeString
     * @param {string} setString
     * @param {string} valueString
     * @param {*} value
     * @returns {void}
     * @memberof ViewManager
     */
    adjustParams(typeString: string, valueString: string, value: any): void  {
        const selectedFeatures = this.view.getSelectedFeatures();
        if (selectedFeatures.length > 0) {
            const correctType = this.getFeaturesOfType(typeString, selectedFeatures);
            if (correctType.length > 0) {
                this.adjustAllFeatureParams(valueString, value, correctType);
            }

            // Check if any components are selected
            // TODO: modify parameters window to not have chain of updates
            // Cycle through all components and connections and change the parameters
            for (const i in this.view.selectedComponents) {
                this.view.selectedComponents[i].updateParameter(valueString, value);
            }
            for (const i in this.view.selectedConnections) {
                this.view.selectedConnections[i].updateParameter(valueString, value);
            }
        } else {
            this.updateDefault(typeString, valueString, value);
        }
    }

    /**
     * Updates the default feature parameter
     * @param {string} typeString
     * @param {string} setString
     * @param {string} valueString
     * @param value
     * @returns {void}
     * @memberof ViewManager
     */
    updateDefault(typeString: string, valueString: string, value: any): void  {
        // Registry.featureDefaults[setString][typeString][valueString] = value;
        const defaults = ComponentAPI.getDefaultsForType(typeString);
        defaults[valueString] = value;
    }

    /**
     * Updates the defaults in the feature
     * @param {Feature} feature Feature object
     * @returns {void}
     * @memberof ViewManager
     */
    updateDefaultsFromFeature(feature: Feature): void  {
        const heritable = feature.getHeritableParams();
        for (const key in heritable) {
            this.updateDefault(feature.getType(), key, feature.getValue(key));
        }
    }

    /**
     * Reverts the feature to default
     * @param {string} valueString
     * @param {Feature} feature
     * @returns {void}
     * @memberof ViewManager
     */
    revertFieldToDefault(valueString: string, feature: Feature): void  {
        feature.updateParameter(valueString, ComponentAPI.getDefaultsForType(feature.getType())[valueString]);
    }

    /**
     * Reverts the feature to params to defaults
     * @param {Feature} feature
     * @returns {void}
     * @memberof ViewManager
     */
    revertFeatureToDefaults(feature: Feature): void  {
        const heritable = feature.getHeritableParams();
        for (const key in heritable) {
            this.revertFieldToDefault(key, feature);
        }
    }

    /**
     * Reverts features to defaults
     * @param {Array} features Features to revert to default
     * @returns {void}
     * @memberof ViewManager
     */
    revertFeaturesToDefaults(features: Feature[]): void  {
        for (const feature of features) {
            this.revertFeatureToDefaults(feature);
        }
    }

    /**
     * Checks if the point intersects with any other feature
     * @param {Array<number>} point Array with the X and Y coordinates
     * @return PaperJS rendered Feature
     * @memberof ViewManager
     */
    hitFeature(point: number[]) {
        return this.view.hitFeature((point as unknown) as paper.Point);
    }

    /**
     * Checks if the point intersects with any other feature
     * @param {string} ID of feature object
     * @return {Feature}
     * @memberof ViewManager
     */

    getFeatureByID(featureID: string) {
        const layer = this.getRenderLayerByID(featureID);
        return layer.getFeature(featureID);
    }

    /**
     * Checks if the point intersects with any other feature
     * @param {string} ID of feature object
     * @return {RenderLayer}
     * @memberof ViewManager
     */
    getRenderLayerByID(featureID: string) {
        for (let i = 0; i < this.renderLayers.length; i++) {
            const layer = this.renderLayers[i];
            if (layer.containsFeatureID(featureID)) {
                return layer;
            }
        }
        // Should textlayer logic be here or in device? (Currently in device)
        // for (let i = 0; i < this.__textLayers.length; i++) {
        //     let layer = this.__textLayers[i];
        //     if (layer.containsFeatureID(featureID)) {
        //         return layer;
        //     }
        // }
        throw new Error("FeatureID " + featureID + " not found in any renderLayer.");
    }

    /**
     * Checks if the element intersects with any other feature
     * @param element
     * @return {*|Array}
     * @memberof ViewManager
     */
    hitFeaturesWithViewElement(element: any): any[] {
        return this.view.hitFeaturesWithViewElement(element);
    }

    /**
     * Activates the given tool
     * @param {string} toolString
     * @param rightClickToolString
     * @returns {void}
     * @memberof ViewManager
     */
    activateTool(toolString: string, rightClickToolString = "SelectTool"): void  {
        if (this.tools[toolString] === null) {
            throw new Error("Could not find tool with the matching string");
        }
        // Cleanup job when activating new tool
        this.view.clearSelectedItems();

        this.mouseAndKeyboardHandler.leftMouseTool = this.tools[toolString];
        this.mouseAndKeyboardHandler.rightMouseTool = this.tools[rightClickToolString];
        this.mouseAndKeyboardHandler.updateViewMouseEvents();
    }

    /**
     * Switches to 2D
     * @returns {void}
     * @memberof ViewManager
     */
    switchTo2D(): void  {
        if (Registry.currentDevice === null){
            throw new Error("No device selected");
        }
        if (this.threeD) {
            this.threeD = false;
            const center = this.renderer.getCameraCenterInMicrometers();
            const zoom = this.renderer.getZoom();
            let newCenterX = center[0];
            if (newCenterX < 0) {
                newCenterX = 0;
            } else if (newCenterX > Registry.currentDevice.getXSpan()) {
                newCenterX = Registry.currentDevice.getXSpan();
            }
            let newCenterY = paper.view.center.y - center[1];
            if (newCenterY < 0) {
                newCenterY = 0;
            } else if (newCenterY > Registry.currentDevice.getYSpan()) {
                newCenterY = Registry.currentDevice.getYSpan();
            }
            // HTMLUtils.setButtonColor(this.__button2D!, Colors.getDefaultLayerColor((Registry.currentLayer as unknown) as Layer), activeText);
            // HTMLUtils.setButtonColor(this.__button3D!, inactiveBackground, inactiveText);
            Registry.viewManager!.setCenter((new paper.Point(newCenterX, newCenterY) as unknown) as number[]);
            Registry.viewManager!.setZoom(zoom);
            // HTMLUtils.addClass(this.__renderBlock!, "hidden-block");
            // HTMLUtils.removeClass(this.__canvasBlock!, "hidden-block");
            // HTMLUtils.removeClass(this.__renderBlock!, "shown-block");
            // HTMLUtils.addClass(this.__canvasBlock!, "shown-block");
        }
    }

    /**
     * Switches to 3D
     * @returns {void}
     * @memberof ViewManager
     */
    switchTo3D(): void  {
        if (!this.threeD) {
            this.threeD = true;
            // setButtonColor(this.__button3D!, Colors.getDefaultLayerColor(Registry.currentLayer as any), activeText);
            // setButtonColor(this.__button2D!!, inactiveBackground, inactiveText);
            this.renderer.loadJSON((Registry.currentDevice as any).toJSON());
            const cameraCenter = this.view.getViewCenterInMillimeters();
            const height = (Registry.currentDevice as any).params.getValue("height") / 1000;
            const pixels = this.view.getDeviceHeightInPixels();
            this.renderer.setupCamera(cameraCenter[0], cameraCenter[1], height, pixels, paper.view.zoom);
            this.renderer.showMockup();
            HTMLUtils.removeClass(this.__renderBlock!, "hidden-block");
            HTMLUtils.addClass(this.__canvasBlock!, "hidden-block");
            HTMLUtils.addClass(this.__renderBlock!, "shown-block");
            HTMLUtils.removeClass(this.__canvasBlock!, "shown-block");
        }
    }

    /**
     * Loads a device from a JSON format when the user drags and drops it on the grid
     * @param selector
     * @returns {void}
     * @memberof ViewManager
     */
    setupDragAndDropLoad(selector: string): void  {
        const dnd = HTMLUtils.DnDFileController(selector, function(files) {
            const f = files[0];

            const reader = new FileReader();
            reader.onloadend = function(e) {
                let result = this.result as string;
                // try {
                let jsonresult = JSON.parse(result);
                Registry.viewManager?.loadDeviceFromJSON((jsonresult as unknown) as InterchangeV1_2);
                // } catch (error) {
                //     console.error(error.message);
                //     alert("Unable to parse the design file, please ensure that the file is not corrupted:\n" + error.message);
                // }
            };
            try {
                reader.readAsText(f);
            } catch (err) {
                console.log("unable to load JSON: " + f);
            }
        });
    }

    /**
     * Closes the params window
     * @returns {void}
     * @memberof ViewManager
     */
    killParamsWindow(): void  {
        const paramsWindow = document.getElementById("parameter_menu");
        if (paramsWindow) paramsWindow.parentElement?.removeChild(paramsWindow);
    }

    /**
     * This method saves the current device to the design history
     * @memberof ViewManager
     * @returns {void}
     */
    saveDeviceState(): void  {
        console.log("Saving to stack");

        // TODO - Future versions of the software should be using the saving
        // the device as a set of atomic operations and not as the json data
        // unless we are using it as a refernce.
        const save = JSON.stringify(Registry.currentDevice?.toInterchangeV1([]));

        this.undoStack.pushDesign(save);
    }

    /**
     * Undoes the recent update
     * @returns {void}
     * @memberof ViewManager
     */
    undo(): void  {
        const previousdesign = this.undoStack.popDesign();
        console.log(previousdesign);
        if (previousdesign) {
            const result = JSON.parse(previousdesign);
            this.loadDeviceFromJSON(result);
        }
    }

    /**
     * Resets the tool to the default tool
     * @returns {void}
     * @memberof ViewManager
     */
    resetToDefaultTool(): void  {
        this.cleanupActiveTools();
        this.activateTool("MouseSelectTool");
        // this.activateTool("RenderMouseTool");
        // this.componentToolBar.setActiveButton("SelectButton");
    }

    /**
     * Runs cleanup method on the activated tools
     * @returns {void}
     * @memberof ViewManager
     */
    cleanupActiveTools(): void  {
        if (this.mouseAndKeyboardHandler.leftMouseTool) {
            this.mouseAndKeyboardHandler.leftMouseTool.cleanup();
        }
        if (this.mouseAndKeyboardHandler.rightMouseTool) {
            this.mouseAndKeyboardHandler.rightMouseTool.cleanup();
        }
    }

    /**
     * VALVE3D features draw at the glyph center. Feature ``position`` is that
     * center (Neptune PR and 3DuF native). Do not add ports[0] / half-span —
     * that double-shifts the gap off the host channel.
     */
    private valveGlyphCenter(_valve: Component, feature: Feature): paper.Point {
        const position = feature.getValue("position");
        return new paper.Point(Number(position[0]), Number(position[1]));
    }

    private getValveFlowClipRect(valve: Component): ValveGapRect | null {
        if (!this.currentDevice) {
            return null;
        }
        for (const featureID of valve.featureIDs) {
            let feature: Feature;
            try {
                feature = this.currentDevice.getFeatureByID(featureID);
            } catch {
                continue;
            }
            if (
                feature.layer &&
                feature.layer.type === LogicalLayerType.FLOW &&
                (feature.getType() === "Valve3D" || feature.getType() === "Valve3D_control")
            ) {
                const center = this.valveGlyphCenter(valve, feature);
                return valveGapRectFromValues(
                    [center.x, center.y],
                    feature.getValue("valveRadius"),
                    feature.getValue("gap"),
                    feature.getValue("rotation")
                );
            }
        }
        try {
            return valveGapRectFromValues(
                valve.getValue("position"),
                valve.getValue("valveRadius"),
                valve.getValue("gap"),
                valve.getValue("rotation")
            );
        } catch {
            return null;
        }
    }

    private getValveFlowGapPath(valve: Component, connection?: Connection): paper.Path.Rectangle | null {
        if (!this.currentDevice) {
            return null;
        }
        for (const featureID of valve.featureIDs) {
            const feature = this.currentDevice.getFeatureByID(featureID);
            if (
                feature.layer &&
                feature.layer.type === LogicalLayerType.FLOW &&
                (feature.getType() === "Valve3D" || feature.getType() === "Valve3D_control")
            ) {
                const radius = feature.getValue("valveRadius");
                const gap = feature.getValue("gap");
                const rotation = feature.getValue("rotation");
                // The channel is rendered with rounded caps that extend channelWidth/2 past
                // each break point. To keep those caps from leaking into the valve area and
                // filling the gap between the two crescents, pad the gap rectangle along the
                // connection direction by channelWidth/2 on each side.
                let axialPadding = 0;
                if (connection) {
                    try {
                        const channelWidth = Number(connection.getValue("channelWidth"));
                        if (Number.isFinite(channelWidth) && channelWidth > 0) {
                            axialPadding = channelWidth / 2;
                        }
                    } catch {
                        axialPadding = 0;
                    }
                }
                const center = this.valveGlyphCenter(valve, feature);
                // rotation 0: valve crescent gap is horizontal, flow is vertical.
                // Pad along Y (channel axis) by channelWidth/2 so rounded end-caps
                // do not fill the 600µm valve gap. Perpendicular span is valveRadius.
                const gapPath = new paper.Path.Rectangle({
                    from: new paper.Point(center.x - radius, center.y - gap / 2 - axialPadding),
                    to: new paper.Point(center.x + radius, center.y + gap / 2 + axialPadding)
                });
                gapPath.rotate(rotation, center);
                return gapPath;
            }
        }
        return null;
    }

    private getValveFlowGapInfo(valve: Component): { center: paper.Point; rotation: number; gap: number } | null {
        if (!this.currentDevice) {
            return null;
        }
        for (const featureID of valve.featureIDs) {
            const feature = this.currentDevice.getFeatureByID(featureID);
            if (
                feature.layer &&
                feature.layer.type === LogicalLayerType.FLOW &&
                (feature.getType() === "Valve3D" || feature.getType() === "Valve3D_control")
            ) {
                const rotation = Number(feature.getValue("rotation"));
                const gap = Number(feature.getValue("gap"));
                if (!Number.isFinite(rotation) || !Number.isFinite(gap) || gap <= 0) {
                    return null;
                }
                return {
                    center: this.valveGlyphCenter(valve, feature),
                    rotation,
                    gap
                };
            }
        }
        return null;
    }

    private insertGapByProjectionInfo(
        connection: Connection,
        gapInfo: { center: paper.Point; rotation: number; gap: number }
    ): boolean {
        const segments = connection.getValue("segments");
        if (!Array.isArray(segments) || segments.length === 0) {
            return false;
        }

        const theta = (gapInfo.rotation * Math.PI) / 180;
        // Valve gap strip is perpendicular to channel axis; channel axis follows this vector.
        const axis = new paper.Point(Math.sin(theta), Math.cos(theta));
        let channelWidth = 0;
        try {
            const cw = Number(connection.getValue("channelWidth"));
            if (Number.isFinite(cw) && cw > 0) {
                channelWidth = cw;
            }
        } catch {
            channelWidth = 0;
        }
        const halfGapAlongAxis = gapInfo.gap / 2 + channelWidth / 2;

        let bestIndex = -1;
        let bestDistance = Number.POSITIVE_INFINITY;
        let bestStart = new paper.Point(0, 0);
        let bestEnd = new paper.Point(0, 0);
        let bestProjection = new paper.Point(0, 0);

        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];
            const start = new paper.Point(segment[0][0], segment[0][1]);
            const end = new paper.Point(segment[1][0], segment[1][1]);
            const v = end.subtract(start);
            const len2 = v.dot(v);
            if (len2 === 0) {
                continue;
            }
            let t = gapInfo.center.subtract(start).dot(v) / len2;
            t = Math.max(0, Math.min(1, t));
            const projection = start.add(v.multiply(t));
            const dist = projection.getDistance(gapInfo.center);
            if (dist < bestDistance) {
                bestDistance = dist;
                bestIndex = i;
                bestStart = start;
                bestEnd = end;
                bestProjection = projection;
            }
        }

        if (bestIndex < 0) {
            return false;
        }

        const candidateA = bestProjection.subtract(axis.multiply(halfGapAlongAxis));
        const candidateB = bestProjection.add(axis.multiply(halfGapAlongAxis));
        const segmentVector = bestEnd.subtract(bestStart);
        const segmentLen2 = segmentVector.dot(segmentVector);
        if (segmentLen2 === 0) {
            return false;
        }

        const projectToSegment = (p: paper.Point): paper.Point => {
            let t = p.subtract(bestStart).dot(segmentVector) / segmentLen2;
            t = Math.max(0, Math.min(1, t));
            return bestStart.add(segmentVector.multiply(t));
        };

        const break1 = projectToSegment(candidateA);
        const break2 = projectToSegment(candidateB);

        const startToBreak1 = bestStart.getDistance(break1);
        const endToBreak2 = bestEnd.getDistance(break2);
        const startToBreak2 = bestStart.getDistance(break2);
        const endToBreak1 = bestEnd.getDistance(break1);

        let nearStart = break1;
        let nearEnd = break2;
        if (startToBreak1 + endToBreak2 > startToBreak2 + endToBreak1) {
            nearStart = break2;
            nearEnd = break1;
        }

        const roundedStart = [Math.round(bestStart.x), Math.round(bestStart.y)] as [number, number];
        const roundedEnd = [Math.round(bestEnd.x), Math.round(bestEnd.y)] as [number, number];
        const roundedBreakStart = [Math.round(nearStart.x), Math.round(nearStart.y)] as [number, number];
        const roundedBreakEnd = [Math.round(nearEnd.x), Math.round(nearEnd.y)] as [number, number];

        if (
            (roundedStart[0] === roundedBreakStart[0] && roundedStart[1] === roundedBreakStart[1]) ||
            (roundedEnd[0] === roundedBreakEnd[0] && roundedEnd[1] === roundedBreakEnd[1])
        ) {
            return false;
        }

        const segment1 = [roundedStart, roundedBreakStart];
        const segment2 = [roundedEnd, roundedBreakEnd];
        const updatedSegments = segments.slice();
        updatedSegments.splice(bestIndex, 1, segment1, segment2);
        connection.updateSegments(updatedSegments);
        return true;
    }

    private insertValveGapByProjection(connection: Connection, valve: Component): boolean {
        const gapInfo = this.getValveFlowGapInfo(valve);
        if (!gapInfo) {
            return false;
        }
        return this.insertGapByProjectionInfo(connection, gapInfo);
    }

    private getUnmappedFlowValveFeaturesForConnection(connection: Connection): Feature[] {
        const candidates: Feature[] = [];
        if (!connection.layer) {
            return candidates;
        }
        const layerFeatures = connection.layer.getAllFeaturesFromLayer();
        for (const featureID in layerFeatures) {
            const feature = layerFeatures[featureID];
            if (feature.referenceID !== null) {
                continue;
            }
            if (feature.getType() !== "Valve3D" && feature.getType() !== "Valve3D_control") {
                continue;
            }
            candidates.push(feature);
        }
        return candidates;
    }

    private getGapInfoFromValveFeature(feature: Feature): { center: paper.Point; rotation: number; gap: number } | null {
        const position = feature.getValue("position");
        const rotation = Number(feature.getValue("rotation"));
        const gap = Number(feature.getValue("gap"));
        if (!position || !Number.isFinite(rotation) || !Number.isFinite(gap) || gap <= 0) {
            return null;
        }
        return {
            center: new paper.Point(position[0], position[1]),
            rotation,
            gap
        };
    }

    private findMappedConnectionForValve(valve: Component): Connection | null {
        if (!this.currentDevice) {
            return null;
        }
        for (const connection of this.currentDevice.connections) {
            const mappedValves = this.currentDevice.getValvesForConnection(connection);
            if (mappedValves.some(mappedValve => mappedValve.id === valve.id)) {
                return connection;
            }
        }
        return null;
    }

    private syncConnectionBreaksForFeatureUpdate(feature: Feature, updatedKey: string | null): void {
        if (!this.currentDevice) {
            return;
        }

        if (
            VALVE_RENDER_TYPES.has(feature.getType()) &&
            feature.referenceID &&
            (updatedKey === null || VALVE_BREAK_RELEVANT_PARAMS.has(updatedKey))
        ) {
            const valve = this.currentDevice.getComponentByID(feature.referenceID);
            if (!valve) {
                return;
            }
            const mappedConnection = this.findMappedConnectionForValve(valve);
            if (!mappedConnection) {
                return;
            }
            try {
                this.updatesConnectionRender(mappedConnection);
            } catch (err) {
                console.warn("Could not sync valve connection break after parameter update:", err);
            }
            return;
        }

        if (feature.getType() === "Connection" && updatedKey === "channelWidth") {
            const connection = this.currentDevice.getConnectionForFeatureID(feature.ID);
            if (!connection) {
                return;
            }
            const valves = this.currentDevice.getValvesForConnection(connection);
            if (!valves || valves.length === 0) {
                return;
            }
            try {
                this.updatesConnectionRender(connection);
            } catch (err) {
                console.warn("Could not sync valve connection break after channel width update:", err);
            }
        }
    }

    /**
     * Updates the renders for all the connection in the blah
     * @returns {void}
     * @memberof ViewManager
     */
    updatesConnectionRender(connection: Connection): void  {
        // First Redraw all the segements without valves or insertions
        connection.regenerateSegments();

        // CONTROL stubs end at the valve / pump pad. Gapping them at the
        // FLOW valve rectangle splits one LFR-width line into two Features.
        if (connection.layer && connection.layer.type === LogicalLayerType.CONTROL) {
            return;
        }

        // Get all the valves for a connection
        const valves = Registry.currentDevice?.getValvesForConnection(connection);

        // Cycle through each of the valves
        if (valves === undefined || valves === null) {
            throw new Error("Valves are undefined or null when updating connection: " + connection.id);
        }
        const gaps: ValveGapRect[] = [];
        for (const j in valves) {
            const valve = valves[j];
            const rect = this.getValveFlowClipRect(valve);
            if (rect) {
                gaps.push(rect);
            }
        }

        // Backward-compatible fallback: some files (or buggy placement paths) have standalone
        // Valve3D features without component/referenceID/valveMap entries. Still break channel.
        const unmappedValveFeatures = this.getUnmappedFlowValveFeaturesForConnection(connection);
        for (const valveFeature of unmappedValveFeatures) {
            const rect = valveGapRectFromValues(
                valveFeature.getValue("position"),
                valveFeature.getValue("valveRadius"),
                valveFeature.getValue("gap"),
                valveFeature.getValue("rotation")
            );
            if (rect) {
                gaps.push(rect);
            }
        }

        if (gaps.length > 0) {
            connection.insertValveGapsGeometric(gaps);
        }
    }

    /**
     * Shows in the UI a message
     * @param {string} message Messsage to display
     * @returns {void}
     * @memberof ViewManager
     */
    showUIMessage(message: string): void  {
        this.messageBox.MaterialSnackbar.showSnackbar({
            message: message
        });
    }

    /**
     * Sets up all the tools to be used by the user
     * @returns {void}
     * @memberof ViewManager
     */
    setupTools(): void  {
        this.tools.MouseSelectTool = new MouseSelectTool(this, this.view);
        // this.tools.RenderMouseTool = new RenderMouseTool(this, this.view);
        this.tools.InsertTextTool = new InsertTextTool(this);
        this.tools.Connection = new ConnectionTool(this, "Connection", "Basic");
        // All the new tools
        this.tools.MoveTool = new MoveTool(this);
        this.tools.GenerateArrayTool = new GenerateArrayTool(this);

    }

    /**
     * Adds a custom component tool
     * @param {string} identifier
     * @returns {void}
     * @memberof ViewManager
     */
    addCustomComponentTool(identifier: string): void  {
        const customcomponent = this.customComponentManager?.getCustomComponent(identifier);
        this.tools[identifier] = new CustomComponentPositionTool(this, customcomponent, "Custom");
    }

    /**
     * Initialize the default placement for components
     * @returns {void}
     * @memberof ViewManager
     */
    __initializeRatsNest(): void  {
        // Step 1 generate features for all the components with some basic layout
        const components = this.currentDevice?.components;
        const xpos = 10000;
        const ypos = 10000;
        for (const i in components) {
            const component = components[parseInt(i)];
            const currentposition = component.getPosition();
            // TODO: Refine this logic, it sucks
            if (currentposition[0] === 0 && currentposition[1] === 0) {
                if (!component.placed) {
                    this.__generateDefaultPlacementForComponent(component, xpos * (parseInt(i) + 1), ypos * (Math.floor(parseInt(i) / 5) + 1));
                }
            } else {
                if (!component.placed) {
                    this.__generateDefaultPlacementForComponent(component, currentposition[0], currentposition[1]);
                }
            }
        }

        // TODO: Step 2 generate rats nest renders for all the components

        this.view.updateRatsNest();
        this.view.updateComponentPortsRender();
    }

    /**
     * Generates the default placement for components
     * @param {Component} component
     * @param {number} xpos Default X coordinate
     * @param {number} ypos Default Y coordinate
     * @returns {void}
     * @memberof ViewManager
     */
    __generateDefaultPlacementForComponent(component: Component, xpos: number, ypos: number): void  {
        const params_to_copy = component.params.toJSON();

        params_to_copy.position = [xpos, ypos];

        // Get default params and overwrite them with json params, this can account for inconsistencies
        const paramsJson = typeof component.params?.toJSON === "function" ? component.params.toJSON() : undefined;
        const renderdefkeys = ComponentAPI.getRenderTypeKeysForMINT(component.mint, paramsJson);
        for (let i = 0; i < renderdefkeys?.length!; i++) {
            const key = renderdefkeys![i];
            const newFeature = Device.makeFeature(key, params_to_copy);
            component.addFeatureID(newFeature.ID);
            this.currentLayer.addFeature(newFeature);
        }

        // Set the component position
        component.updateComponentPosition([xpos, ypos]);
    }

    /**
     * Generates a JSON format file to export it
     * @returns {void}
     * @memberof ViewManager
     */
    generateExportJSON(errorList:Array<SerializationError> = []): InterchangeV1_2  {
        const json = ExportUtils.toInterchangeV1_2(this, errorList);
        // const json = this.currentDevice.toInterchangeV1_1();
        // json.customComponents = this.customComponentManager.toJSON();
        return json;
    }

    /**
     * This method attempts to load any custom components that are stored in the custom components property
     * @param json
     */
    loadCustomComponents(json: { [key: string]: any }): void  {
        if (Object.prototype.hasOwnProperty.call(json, "customComponents")) {
            this.customComponentManager?.loadFromJSON(json.customComponents);
        }
    }

    /**
     * Activates DAFD plugin
     * @param {*} params
     * @returns {void}
     * @memberof ViewManager
     */
    activateDAFDPlugin(params: any = null): void  {
        this.loadDeviceFromJSON(JSON.parse(Examples.dafdtemplate));

        if (params === null) {
            params = {
                orificeSize: 750,
                orificeLength: 200,
                oilInputWidth: 800,
                waterInputWidth: 900,
                outputWidth: 900,
                outputLength: 500,
                height: 100
            };
        }

        DAFDPlugin.fixLayout(params);
    }

    /**
     * This is the method we need to call to fix the valvemaps
     * @memberof ViewManager
     */
    createValveMapFromSelection():void {
        // TODO: Run through the current selection and generate the valve map for every
        // vavle that is in the Selection
        const selection = this.tools.MouseSelectTool.currentSelection;
        const valves = [];
        let connection = null;
        // TODO: run though the items
        for (const render_element of selection) {
            // Check if render_element is associated with a VALVE/VALVE3D
            const component = this.currentDevice!.getComponentForFeatureID(render_element.featureID);
            if (component !== null) {
                console.log("Component Type:", component.mint);
                const type = (component as any).getType();
                if (type === "VALVE3D" || type === "VALVE") {
                    valves.push(component);
                }
            }

            connection = this.currentDevice!.getConnectionForFeatureID(render_element.featureID);
        }

        // Add to the valvemap
        for (const valve of valves) {
            let valve_type = ValveType.NORMALLY_OPEN;
            if (valve.mint === "VALVE3D") {
                valve_type = ValveType.NORMALLY_CLOSED;
            }
            console.log("Adding Valve: ", valve);
            this.currentDevice!.insertValve(valve, connection!, valve_type);
        }
    }

    /**
     * Activates the corresponding placement tool for the given type of component and returns the active tool
     * @param {*} minttype
     * @returns
     */
    activateComponentPlacementTool(minttype: string, currentParameters: any): PositionTool | ValveInsertionTool | ConnectionTool {
        const threeduftype = ComponentAPI.getTypeForMINT(minttype);
        if (threeduftype === null) {
            throw new Error("Found null when looking for MINT Type");
        }
        // Cleanup job when activating new tool
        this.view.clearSelectedItems();

        const prevLeft = this.mouseAndKeyboardHandler.leftMouseTool;
        if (prevLeft instanceof ConnectionTool) {
            EventBus.get().emit(EventBus.CONNECTION_ESC_HINT, false);
        }

        let activeTool = null;
        const renderer = ComponentAPI.getRendererForMINT(minttype);
        if (renderer.placementTool === "componentPositionTool") {
            activeTool = new ComponentPositionTool(this, threeduftype , "Basic", currentParameters);
        } else if (renderer.placementTool === "customComponentPositionTool") {
            // activeTool = new CustomComponentPositionTool(ComponentAPI.getTypeForMINT(minttype), "Basic");
        } else if (renderer.placementTool === "positionTool") {
            activeTool = new PositionTool(this, threeduftype, currentParameters);
        } else if (renderer.placementTool === "multilayerPositionTool") {
            activeTool = new MultilayerPositionTool(this, threeduftype, "Basic", currentParameters);
        } else if (renderer.placementTool === "valveInsertionTool") {
            activeTool = new ValveInsertionTool(this, minttype, "Basic", currentParameters);
        } else if (renderer.placementTool === "CellPositionTool") {
            activeTool = new CellPositionTool(this, threeduftype, "Basic", currentParameters);
        } else if (renderer.placementTool === "multilevelPositionTool") {
            // TODO: Add pop up window when using the multilevel position tool to get layer indices
            activeTool = new MultilevelPositionTool(this, threeduftype, "Basic", currentParameters);
            throw new Error("multilevel position tool ui/input elements not set up");
        }else if(renderer.placementTool === "connectionTool"){
            activeTool = new ConnectionTool(this, threeduftype, "Basic");
        }

        if (activeTool === null) {
            throw new Error(`Could not initialize the tool ${minttype}`);
        }

        this.mouseAndKeyboardHandler.leftMouseTool = activeTool;
        this.mouseAndKeyboardHandler.rightMouseTool = activeTool;
        this.mouseAndKeyboardHandler.updateViewMouseEvents();

        return activeTool;
    }

    /**
     * Deactivates the current placement tool
     *
     * @memberof ViewManager
     */
    deactivateComponentPlacementTool(): void {
        console.log("Deactivating Component Placement Tool");
        const left = this.mouseAndKeyboardHandler.leftMouseTool;
        const right = this.mouseAndKeyboardHandler.rightMouseTool;
        if (left != null) {
            left.deactivate();
        }
        if (right != null && right !== left) {
            right.deactivate();
        }
        this.resetToDefaultTool();
    }

    /**
     * Returns the spacing for the current grid on the device
     *
     * @returns
     * @memberof ViewManager
     */
    getGridSize(): number {
        if (this.__grid === null) {
            return 0;
        }
        return this.__grid.spacing;
    }

    /**
     * Updated the grid size for the current view
     *
     * @param {number} value
     * @memberof ViewManager
     */
    updateGridSpacing(value: number):void {
        this.__grid.updateGridSpacing(value);
        this.updateGrid();
    }

    /**
     * Starts the download sequence for the current device
     *
     * @memberof ViewManager
     */
    downloadJSON(): void  {
        if(this.currentDevice === null){
            throw new Error("No device loaded");
        }
        const errorList: Array<SerializationError> = [];
        const fullJson = this.generateExportJSON(errorList);
        const name = this.currentDevice.name || "device";
        const fullText = JSON.stringify(fullJson);
        const portsJson = stripToFlowControlPorts(fullJson);
        const zip = new JSZip();
        zip.file(`${name}.json`, fullText);
        zip.file(`${name}_ports.json`, JSON.stringify(portsJson));
        zip.generateAsync({ type: "blob" }).then(blob => {
            saveAs(blob, `${name}_json.zip`);
        }).catch(err => {
            console.error("[Export JSON]", err);
            const message = err && err.message ? err.message : String(err);
            alert(`Unable to export JSON: ${message}`);
        });

        if (errorList.length > 0) {
            // Concatenate all the errors into a single string with newlines and save it as a file
            let errorString = "";
            for (const error of errorList) {
                errorString += error.toText() + "\n\n";
            }
            const errorBlob = new Blob([errorString], {
                type: "text/plain"
            });
            saveAs(errorBlob, this.currentDevice.name + "_errors.txt");
        }
    }

    findImportedComponentById(id: string) {
        return lookupImportedComponent(this.importedSourceJson, id);
    }

    findImportedConnectionById(id: string) {
        return lookupImportedConnection(this.importedSourceJson, id);
    }

    resolveComponentResetParams(component: Component | null | undefined): { [key: string]: number } | null {
        if (!component) return null;
        const source = this.findImportedComponentById(component.id);
        return resolveResetParams(component.mint, source && source.params, !!source);
    }

    resolveConnectionResetParams(connection: Connection | null | undefined): { [key: string]: number } | null {
        if (!connection) return null;
        const source = this.findImportedConnectionById(connection.id);
        return resolveResetParams("CHANNEL", source && source.params, !!source);
    }

    createNewDevice(name: string): void {
        this.importedSourceJson = null;
        let device = new Device({"x-span": 135000, "y-span": 85000}, name);
        console.log("Created new device: ", device.getXSpan(), device.getYSpan());
        this.clear();
        this.__currentDevice = device;
        this.resetPortsOnlyView();
        // TODO -  Clean up lifecyle for active rendered layers
        this.activeRenderLayerIndex = 0;
        this.createNewLayerBlock();
        // TODO - Generate render layers
        generateRenderLayers(this.__currentDevice);
        this.setNameMap();

        Registry.currentDevice = device;

        this.activeRenderLayerIndex = 0;

        this.addDevice(device);

        // In case of MINT exported json, generate layouts for rats nests
        this.__initializeRatsNest();

        this.view.initializeView();
        this.updateGrid();
        this.updateDevice(Registry.currentDevice!);
        this.refresh(true);
        this.currentLayer = this.renderLayers[0];
        this.updateActiveLayer();

    }

    /**
     * Ridiculously convoluted method to return the render layers corresponding to the current level
     * To be used in the case of creating multi-level components
     * @returns {[any, any, any]}
     * @memberof ViewManager
     */
    getCurrentLevelRenderLayers(): [RenderLayer | null , RenderLayer | null , RenderLayer | null] {
        // Get the current level
        const currentLevel = this.activeRenderLayerIndex % 3;
        let currentFlowLayerIndex = currentLevel * 3 + 0;
        let currentControlLayerIndex = currentLevel * 3 + 1;
        let currentIntegrationLayerIndex = currentLevel * 3 + 2;
        return [this.renderLayers[currentFlowLayerIndex], this.renderLayers[currentControlLayerIndex], this.renderLayers[currentIntegrationLayerIndex]];
    }


}
